import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import {
  closeModal,
  resetPasswordReset,
  resetLoginError,
  selectBrand,
  selectCustomer,
  selectResetPassword,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import {
  SendResetForm,
  ButtonLink,
  Button,
  LoginForm,
  ModalContent,
} from 'components'

const messaging: {
  [key: string]: { title: string; subtitle: string; reset?: string }
} = {
  login: {
    title: 'Log into your account',
    subtitle: "Don't have an account?",
    reset: 'Forget your password?',
  },
  thanx: {
    title: 'Log into your account',
    subtitle:
      "Please enter your email address, and we'll send you an email with a magic link that logs you into your account automatically.",
  },
  reset: {
    title: 'Reset your password',
    subtitle: 'Please enter the email address associated with your account',
    reset: 'Nevermind, I remembered it',
  },
  resetSent: {
    title: 'Password reset email sent',
    subtitle:
      'A reset password email was just sent to the email address you provided. Please check your inbox and click on the link in the email in order to reset your password.',
    reset: 'Back to login form',
  },
}

const LoginModalClose = styled.div`
  label: LoginModalClose;

  display: flex;
  justify-content: center;
  align-items: center;
`

const LoginModalFooter = styled.div`
  label: LoginModalFooter;

  display: flex;
  justify-content: center !important;
  align-items: center;
  font-size: ${(props) => props.theme.fonts.sizes.small};
`

const LoginEmail = ({
  callback,
  isForgotPassword = false,
  requiredEmail,
}: {
  callback?: () => void
  isForgotPassword?: boolean
  requiredEmail?: string
}) => {
  const [isReset, setIsReset] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { has_thanx } = useAppSelector(selectBrand) || {}
  const { auth } = useAppSelector(selectCustomer)
  const { resetSent } = useAppSelector(selectResetPassword)
  const mode = has_thanx
    ? 'thanx'
    : resetSent
    ? 'resetSent'
    : isReset
    ? 'reset'
    : 'login'
  const msg = messaging[mode]

  const toggleReset = useCallback(() => {
    if (isForgotPassword && isReset) {
      dispatch(closeModal())
      return
    }
    setIsReset(!isReset)
  }, [dispatch, isForgotPassword, isReset])

  const toggleResetSent = () => {
    setIsReset(false)
    dispatch(resetPasswordReset())
  }

  const signUp = () => {
    dispatch(closeModal())
    navigate(`/signup`)
  }

  useEffect(() => {
    if (isForgotPassword) {
      toggleReset()
    }
  }, [isForgotPassword, toggleReset])

  useEffect(() => {
    if (auth) dispatch(closeModal())
    dispatch(resetPasswordReset())
    dispatch(resetLoginError())
    return () => {
      dispatch(resetPasswordReset())
      dispatch(resetLoginError())
    }
  }, [auth, dispatch])

  return (
    <ModalContent
      title={msg.title}
      subtitle={
        <>
          <p>
            {msg.subtitle}{' '}
            {mode === 'login' && (
              <ButtonLink onClick={signUp}>Sign up here.</ButtonLink>
            )}
          </p>
          {mode === 'thanx' && (
            <p>
              Don't have an account yet?{' '}
              <ButtonLink onClick={signUp}>Sign up here.</ButtonLink>
            </p>
          )}
        </>
      }
      footer={
        !has_thanx ? (
          <LoginModalFooter>
            <ButtonLink onClick={resetSent ? toggleResetSent : toggleReset}>
              {msg.reset}
            </ButtonLink>
          </LoginModalFooter>
        ) : undefined
      }
    >
      {resetSent ? (
        <LoginModalClose>
          <Button onClick={() => dispatch(closeModal())}>Close</Button>
        </LoginModalClose>
      ) : isReset ? (
        <SendResetForm callback={callback} />
      ) : (
        <LoginForm callback={callback} requiredEmail={requiredEmail} />
      )}
    </ModalContent>
  )
}

export default LoginEmail
